import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, Calendar, Ticket } from "lucide-react";
import { POLES, ASSOC, DISCIPLINES, INSCRIPTIONS_URL } from "@/lib/data";
import { http } from "@/lib/api";
import EventBookingButton from "@/components/EventBookingButton";
import { getSiteSettings } from "@/lib/site";
import { HOME } from "@/constants/testIds";

const HERO_SLIDES = [
  "/images/hero-sig.webp",
  "/images/prestations-16.webp",
  "/images/jda-8.jpg",
  "/images/queens-1.webp",
  "/images/prestations-10.jpg",
  "/images/jda-1.webp",
  "/images/prestations-14.jpg",
];

const CITY_IMG = {
  "Fresnes": "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxkYW5jZWhhbGwlMjBkYW5jZXIlMjB1cmJhbiUyMHN0cmVldCUyMHN0eWxlJTIwbW92ZW1lbnR8ZW58MHx8fHwxNzg4MDQ3OTQ3fDA&ixlib=rb-4.1.0&q=85",
  "L'Haÿ-les-Roses": "https://images.unsplash.com/photo-1555489401-79c274997434?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxraXpvbWJhJTIwc2Fsc2ElMjBjb3VwbGUlMjBkYW5jaW5nJTIwYmxhY2t8ZW58MHx8fHwxNzg4MDQ3OTM0fDA&ixlib=rb-4.1.0&q=85",
  "Chevilly-Larue": "https://images.unsplash.com/photo-1590022171548-07e6d45025e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxoZWVscyUyMGNob3Jlb2dyYXBoeSUyMGRhbmNlciUyMHNpbGhvdWV0dGUlMjBtb3ZlbWVudHxlbnwwfHx8fDE3ODgwNDgzNzJ8MA&ixlib=rb-4.1.0&q=85",
};

const CITY_KEYS = {
  "Fresnes": "city_fresnes",
  "L'Haÿ-les-Roses": "city_lhay",
  "Chevilly-Larue": "city_chevilly",
};

const QUICK = [
  { label: "Enfants", to: "/planning?audience=enfants" },
  { label: "Ados", to: "/planning?audience=ados" },
  { label: "Adultes", to: "/planning?audience=adultes" },
  { label: "Danses solo", to: "/planning?category=urban" },
  { label: "Danses de couple", to: "/planning?category=couple" },
  { label: "Fitness / Training", to: "/planning?category=fitness" },
];

export default function Home() {
  const [events, setEvents] = React.useState([]);
  const [featured, setFeatured] = React.useState([]);
  const [news, setNews] = React.useState([]);
  const [partners, setPartners] = React.useState([]);
  const [settings, setSettings] = React.useState({});
  React.useEffect(() => {
    http.get("/events").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const alive = (e) => (e.end_date || e.date) >= today;
      setEvents(r.data.filter((e) => alive(e) && e.date <= "2026-09-30").sort((a, b) => a.date.localeCompare(b.date)).slice(0, 3));
      setFeatured(r.data.filter((e) => e.featured && alive(e)));
    }).catch(() => setEvents([]));
    http.get("/news").then((r) => setNews(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => {});
    http.get("/partners").then((r) => setPartners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getSiteSettings().then(setSettings).catch(() => {});
  }, []);

  const heroSlides = settings.images?.hero ? [settings.images.hero] : HERO_SLIDES;
  const [slide, setSlide] = React.useState(0);
  React.useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 4500);
    return () => clearInterval(t);
  }, [heroSlides.length]);
  const cityImg = (c) => settings.images?.[CITY_KEYS[c]] || CITY_IMG[c];
  const poleImg = (p) => settings.images?.[p.slug] || p.image;

  return (
    <div data-testid={HOME.hero}>
      {/* HERO */}
      <section className="relative h-[80vh] min-h-[520px] w-full overflow-hidden bg-black he-grain">
        {heroSlides.map((src, i) => (
          <img key={src} src={src} alt={i === 0 ? "Heel'Events — danse, spectacles et événements" : ""} data-testid={`hero-slide-${i + 1}`} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === slide ? "opacity-60" : "opacity-0"}`} />
        ))}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-5 right-5 z-10 flex gap-2">
            {heroSlides.map((src, i) => (
              <button key={src} type="button" aria-label={`Photo ${i + 1}`} data-testid={`hero-dot-${i + 1}`} onClick={() => setSlide(i)} className={`w-2 h-2 rounded-full transition-colors ${i === slide ? "bg-[var(--he-red)]" : "bg-white/40 hover:bg-white/70"}`} />
            ))}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="relative max-w-7xl mx-auto h-full px-4 sm:px-6 flex flex-col justify-end pb-16 sm:pb-20 text-white">
          <span className="he-overline text-white/80">Depuis 2012 · Val-de-Marne</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase mt-4 max-w-4xl leading-[0.95]">
            De l’école <br />à la scène, <span className="text-[var(--he-red)]">une même passion :</span> danser ensemble.
          </h1>
          <p className="mt-5 max-w-xl text-white/80 font-body text-base sm:text-lg">
            {settings.texts?.hero_sub || "Danse • Formation • Scène • Événements • Partage"}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cours" data-testid={HOME.ctaInscription} className="btn-red rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
              Découvrir nos cours <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/planning" data-testid={HOME.ctaPlanning} className="rounded-full px-6 py-3 text-sm border border-white/70 hover:bg-white hover:text-black transition-colors">
              Voir le planning 2026-2027
            </Link>
          </div>
        </div>
      </section>

      {/* À la une : événements épinglés via l'admin */}
      {featured.length > 0 && (
        <section data-testid="home-featured-events" className="bg-white border-b border-black/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <p className="he-overline text-neutral-500 mb-6">À ne pas manquer</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {featured.map((e) => (
                <article key={e.id} data-testid="home-featured-card" className="flex flex-col sm:flex-row border border-black/10 rounded-2xl overflow-hidden hover:border-[var(--he-red)] transition-colors">
                  {e.image ? (
                    <div className="sm:w-48 shrink-0 aspect-[16/10] sm:aspect-auto bg-black overflow-hidden">
                      <img src={e.image} alt={e.name} className="w-full h-full object-cover" />
                    </div>
                  ) : null}
                  <div className="p-5 flex flex-col">
                    <div className="he-overline text-neutral-500">{e.date}{e.time ? ` · ${e.time}` : ""}</div>
                    <h3 className="font-heading text-2xl font-black tracking-tight mt-2">{e.name}</h3>
                    {e.location && <p className="mt-2 text-sm text-neutral-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--he-red)]" /> {e.location}</p>}
                    <div className="mt-auto pt-4">
                      {e.widget_url ? (
                        <EventBookingButton url={e.widget_url} testid="home-featured-cta" label={/portes ouvertes/i.test(e.name || "") ? "Participer" : "Réserver ma place"} />
                      ) : e.booking_url ? (
                        <a href={e.booking_url} target="_blank" rel="noreferrer" data-testid="home-featured-cta" className="btn-red rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2">
                          <Ticket className="w-4 h-4" /> {/portes ouvertes/i.test(e.name || "") ? "Participer" : "Réserver"}
                        </a>
                      ) : (
                        <span className="text-xs text-neutral-400 uppercase tracking-widest font-bold">Billetterie bientôt disponible</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Baseline */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        <p className="he-overline text-neutral-500">L’Association</p>
        <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-3">
          {settings.texts?.home_intro || "Bien plus qu’une école de danse."}
        </h2>
        <p className="mt-5 text-neutral-600 max-w-2xl mx-auto font-body">
          Heel’Events rassemble plusieurs univers artistiques et culturels. Une école, une compagnie, des projets solidaires et un espace pour les artistes.
        </p>
      </section>

      {/* Bento poles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5">
          {POLES.map((p, i) => (
            <Link
              key={p.slug}
              to={`/poles/${p.slug}`}
              data-testid={`pole-card-${p.slug}`}
              className={`group relative overflow-hidden rounded-2xl border border-black/10 bg-black text-white block
                ${i === 0 ? "md:col-span-8 md:row-span-2 aspect-[16/10] md:aspect-auto md:min-h-[520px]" : "md:col-span-4 aspect-[16/11]"}
              `}
            >
              <img src={poleImg(p)} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
              <div className="relative p-6 sm:p-8 h-full flex flex-col justify-end">
                {p.logo && (
                  <img src={p.logo} alt="" className={`mb-4 bg-white/80 backdrop-blur-md rounded-full px-4 py-2 object-contain shadow-[0_8px_30px_rgba(0,0,0,0.35)] ring-1 ring-white/50 ${i === 0 ? "max-w-[200px] max-h-16" : "max-w-[140px] max-h-12"}`} />
                )}
                <span className="he-overline text-white/70">Pôle {i + 1}</span>
                <h3 className={`font-heading font-black tracking-tighter mt-2 ${i === 0 ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>{p.name}</h3>
                <p className="mt-2 text-white/80 text-sm max-w-md font-body">{p.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-[var(--he-red)] font-bold text-sm">Découvrir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Afrolat'Studio quick access */}
      <section className="bg-[var(--he-bg-dark)] text-white py-20 sm:py-28 he-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <p className="he-overline text-neutral-400">Afrolat’Studio</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-3">Trouvez le cours qui vous ressemble.</h2>
            </div>
            <Link to="/cours" className="text-sm text-neutral-300 hover:text-white link-underline">Voir tous les cours →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {QUICK.map((q) => (
              <Link key={q.label} to={q.to} className="group p-6 border border-white/10 rounded-xl hover:border-[var(--he-red)] transition-colors">
                <span className="font-heading font-bold uppercase tracking-tight text-sm group-hover:text-[var(--he-red)] transition-colors">{q.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="text-center mb-10">
          <p className="he-overline text-neutral-500">Nos lieux</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-3">Retrouvez-nous dans le Val-de-Marne</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {ASSOC.cities.map((c) => (
            <Link key={c} to={`/planning?city=${encodeURIComponent(c)}`} className="group relative overflow-hidden rounded-2xl aspect-[4/5] bg-black text-white block">
              <img src={cityImg(c)} alt={c} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6">
                <MapPin className="w-5 h-5 mb-3 text-[var(--he-red)]" />
                <h3 className="font-heading text-3xl font-black tracking-tighter uppercase">{c}</h3>
                <span className="mt-2 text-sm text-white/80">Voir les cours →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Agenda */}
      {events.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <p className="he-overline text-neutral-500">Agenda</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Prochains événements</h2>
            </div>
            <Link to="/evenements" className="text-sm link-underline">Tout l’agenda →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {events.map((e) => (
              <article key={e.id} data-testid="home-event-card" className="border border-black/10 rounded-2xl p-5 flex flex-col hover:border-[var(--he-red)] transition-colors">
                <div className="he-overline text-neutral-500">{e.date}{e.time ? ` · ${e.time}` : ""}</div>
                <h3 className="font-heading text-xl font-bold mt-2">{e.name}</h3>
                {e.location && <p className="mt-2 text-sm text-neutral-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--he-red)]" /> {e.location}</p>}
                <div className="mt-5 pt-4 border-t border-black/5 mt-auto flex gap-2">
                  {e.widget_url ? (
                    <EventBookingButton url={e.widget_url} testid="home-event-booking" label={/portes ouvertes/i.test(e.name || "") ? "Participer" : "Réserver ma place"} />
                  ) : e.booking_url ? (
                    <a href={e.booking_url} target="_blank" rel="noreferrer" data-testid="home-event-booking" className="btn-red rounded-full px-4 py-2 text-xs inline-flex items-center gap-2">
                      <Ticket className="w-3.5 h-3.5" /> {/portes ouvertes/i.test(e.name || "") ? "Participer" : "Réserver"}
                    </a>
                  ) : (
                    <span className="text-xs text-neutral-400 uppercase tracking-widest font-bold">Billetterie bientôt disponible</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Actualités */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <p className="he-overline text-neutral-500">Actualités</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Les dernières nouvelles</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {news.map((n) => (
              <article key={n.id} data-testid="home-news-card" className="border border-black/10 rounded-2xl overflow-hidden hover:border-[var(--he-red)] transition-colors">
                {n.image ? (
                  <div className="aspect-[16/9] bg-neutral-100 overflow-hidden">
                    <img src={n.image} alt={n.title} className="w-full h-full object-cover" />
                  </div>
                ) : null}
                <div className="p-5">
                  <div className="he-overline text-neutral-500">{n.date}</div>
                  <h3 className="font-heading text-xl font-bold mt-2">{n.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 font-body line-clamp-3">{n.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Disciplines strip */}
      <section className="border-t border-black/10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-heading text-2xl font-bold tracking-tight">Nos disciplines</h3>
            <Link to="/cours" className="text-sm link-underline">Voir tout →</Link>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4">
            {DISCIPLINES.map((d) => (
              <Link key={d.slug} to="/cours" className="shrink-0 border border-black/10 rounded-full px-5 py-2 text-sm hover:border-[var(--he-red)] hover:text-[var(--he-red)] transition-colors">
                {d.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Team teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="text-center">
          <p className="he-overline text-neutral-500">L’équipe</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-3">Une équipe, des univers, une même passion.</h2>
          <Link to="/equipe" className="mt-6 inline-block link-underline text-sm">Découvrir l’équipe →</Link>
        </div>
      </section>

      {/* Partenaires */}
      {partners.length > 0 && (
        <section data-testid="home-partners" className="max-w-7xl mx-auto px-4 sm:px-6 pb-24 text-center">
          <p className="he-overline text-neutral-500">Ils nous ont fait confiance</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {partners.map((p) => {
              const content = p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="h-12 w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" />
              ) : (
                <span className="font-heading font-bold text-neutral-400 uppercase tracking-wide">{p.name}</span>
              );
              return p.website ? (
                <a key={p.id} href={p.website} target="_blank" rel="noreferrer" title={p.name}>{content}</a>
              ) : (
                <span key={p.id} title={p.name}>{content}</span>
              );
            })}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-black text-white py-24 he-grain">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter uppercase">Prêt·e à danser ?</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto font-body">
            Rejoignez Heel’Events pour la saison 2026-2027. Cours d’essai possible, tous niveaux acceptés.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={INSCRIPTIONS_URL} target="_blank" rel="noreferrer" className="btn-red rounded-full px-8 py-3">Je m’inscris</a>
            <Link to="/planning" className="rounded-full px-8 py-3 border border-white/70 hover:bg-white hover:text-black transition-colors flex items-center gap-2"><Calendar className="w-4 h-4" /> Voir le planning</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
